import { useCallback, useId, useState } from "react";

import { useTabDrafts } from "@/components/ui/box-draft";

import type { Blocker, Effect, Part, Result, RowTask } from "@/components/ui/box.schema";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { RegisterSession } from "@/lib/api/attendance";
import { ApiError } from "@/lib/api/client";
import { checkCommit } from "@/lib/api/commitSeams";
import { sessionPortalApi, withheldNotice, type SessionChangeKind } from "@/lib/api/sessionPortal";
import { classTitle } from "@/lib/class-name";
import { zoneCity } from "@/lib/seat-workspace";
import { formatInCourseZone } from "@/lib/session-portal";

import { ALL_SESSIONS_COPY } from "./session-copy";

const copy = ALL_SESSIONS_COPY.change;
const imam = { system: "imam" } as const;

export type { SessionChangeKind };

interface ChangeDraft {
  note: string;
  email: boolean;
  date: string;
  time: string;
}

/** A send kept with its draft until its answer: its key, what it carried and what it said it would do (W1). */
interface ChangeSend {
  key: string;
  sent: ChangeDraft;
  body: Parameters<typeof sessionPortalApi.change>[3];
  effects: string[];
}

const NOT_DONE: Record<SessionChangeKind, string> = { move: "Not moved", cancel: "Not cancelled", reinstate: "Not reinstated" };

/** The session's start as the course's wall clock, "14:30", which a move starts from. */
function startsLocal(session: RegisterSession, timezone: string): string {
  if (!session.starts_at) return "";
  return new Intl.DateTimeFormat("en-GB", { timeZone: timezone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(session.starts_at));
}

/** A course-local date and wall time as words: "Tue, Oct 14, 10:00 AM". */
function wallWhen(date: string, time: string): string {
  const day = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" }).format(new Date(`${date}T12:00:00Z`));
  const [hours, minutes] = time.split(":").map(Number);
  return `${day}, ${hours % 12 || 12}:${String(minutes).padStart(2, "0")} ${hours < 12 ? "AM" : "PM"}`;
}

function sessionWhen(session: RegisterSession, timezone: string): string {
  return session.starts_at
    ? formatInCourseZone(session.starts_at, timezone, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : wallWhen(session.session_date, "12:00").replace(/, 12:00 PM$/, "");
}

/**
 * A session's Move, Cancel or Reinstate, opened in its row of All sessions
 * (K3, O1): the new time for a move, the note students read, and one commit
 * under its key (W1). A refusal keeps the draft and says the server's reason;
 * an answer that never came offers Check, never a second send.
 */
export function useSessionChangeTasks({ courseId, timezone, unpublished = false, onChanged }: {
  courseId: string;
  timezone: string;
  /** A draft course tells no one, so no email is offered (CC2); the server enforces it. */
  unpublished?: boolean;
  /** Reads the register and the pages a change moves; awaited before the result is drawn. */
  onChanged: () => Promise<unknown>;
}): (session: RegisterSession, kind: SessionChangeKind, rowKey: string) => RowTask {
  // Each row's draft and its unanswered send are kept in this tab, bound to the session's version (O7, CASE-038, R4).
  const ofDraft = useTabDrafts<ChangeDraft, ChangeSend>("all-sessions:change");
  const [results, setResults] = useState<Readonly<Record<string, Result>>>({});
  const [savedNotes, setSavedNotes] = useState<Readonly<Record<string, string>>>({});
  const id = useId();

  return useCallback((session: RegisterSession, kind: SessionChangeKind, rowKey: string): RowTask => {
    const slot = `${session.id}:${kind}`;
    const start = startsLocal(session, timezone);
    const initial: ChangeDraft = { note: "", email: true, date: session.session_date, time: start };
    const kept = ofDraft({ record: slot, version: session.updated_at, initial });
    const draft = kept.value;
    const edit = (next: Partial<ChangeDraft>) => kept.set((was) => ({ ...was, ...next }));
    const settle = (next: Result) => {
      setResults((current) => ({ ...current, [slot]: next }));
      return next;
    };
    const name = classTitle(session, timezone);
    const when = sessionWhen(session, timezone);
    const whenPart = `${rowKey}:when`;
    const notePart = `${rowKey}:note`;
    const saved = results[slot]?.state === "confirmed" && !draft.note.trim() ? savedNotes[slot] : undefined;
    const field = `${id}-${slot}`;
    const unchanged = draft.date === session.session_date && draft.time === start;
    const target = draft.date && draft.time ? wallWhen(draft.date, draft.time) : null;

    // The session changed after this draft began: compare, then keep the draft or take the session as it is (R4).
    const conflict = kept.conflict ? {
      sentence: copy.changedSince(name),
      facts: [{ label: copy.nowWhen, theirs: when }] as [{ label: string; theirs: string }],
      keepMine: { label: copy.keepMine, run: () => { kept.keepMine(); settle({ state: "none" }); } },
      loadNewer: { label: copy.loadNewer, run: () => { kept.loadNewer(); settle({ state: "none" }); } },
    } : undefined;
    const parts: Part[] = [
      ...(kind === "move" ? [{
        id: whenPart,
        label: copy.when,
        state: unchanged || !target ? { class: "waiting", word: copy.when } : { class: "draft", word: target },
        owner: imam,
        fold: "never",
        draft: "browser",
        keep: kept,
        conflict,
        render: () => (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1.5"><Label htmlFor={`${field}-date`}>{copy.date}</Label><Input id={`${field}-date`} type="date" value={draft.date} onChange={(event) => edit({ date: event.target.value })} /></div>
            <div className="grid gap-1.5"><Label htmlFor={`${field}-time`}>{copy.time}</Label><Input id={`${field}-time`} type="time" value={draft.time} onChange={(event) => edit({ time: event.target.value })} /></div>
            <p className="text-meta text-muted-foreground sm:col-span-2">{copy.keepsLength(zoneCity(timezone))}</p>
          </div>
        ),
      } satisfies Part] : []),
      {
        id: notePart,
        label: copy.note,
        state: draft.note.trim() ? { class: "draft", word: "Written" } : saved ? { class: "done", word: copy.noteSaved } : { class: "waiting", word: "Not written" },
        owner: imam,
        fold: "never",
        draft: "browser",
        keep: kept,
        ...(kind === "move" ? {} : { conflict }),
        render: () => (
          <div className="grid gap-1.5">
            {saved && <p dir="auto" className="whitespace-pre-wrap">{copy.savedNote(saved)}</p>}
            <Label htmlFor={`${field}-note`}>{copy.note}</Label>
            <Textarea id={`${field}-note`} value={draft.note} maxLength={200} rows={3} aria-describedby={`${field}-note-help`} onChange={(event) => edit({ note: event.target.value })} />
            <p id={`${field}-note-help`} className="text-meta text-muted-foreground">{copy.noteHelp}</p>
          </div>
        ),
      },
    ];

    const effects = (): Effect[] => [
      kind === "move"
        ? { id: "moves", sentence: copy.moves(name, `${target ?? when}, ${zoneCity(timezone)}`), part: whenPart }
        : kind === "cancel"
          ? { id: "cancels", sentence: copy.cancels(name, when) }
          : { id: "reinstates", sentence: copy.reinstates(name, when) },
      ...(kind === "move" ? [{ id: "keeps", sentence: copy.keeps }] : []),
      ...(kind === "move" && session.status === "cancelled" ? [{ id: "back", sentence: copy.reinstatesToo }] : []),
      ...(unpublished
        ? [{ id: "reads", sentence: copy.readsNoteLater, part: notePart }, { id: "email", sentence: copy.noEmail }]
        : [{ id: "reads", sentence: copy.readsNote, part: notePart }, { id: "email", sentence: copy.email, optional: { checked: draft.email, toggle: () => edit({ email: !draft.email }) } }]),
    ];
    const blockers = (): Blocker[] => [
      ...(kind === "move" && !draft.date ? [{ kind: "missing", part: whenPart, sentence: copy.dateMissing } as const] : []),
      ...(kind === "move" && !draft.time ? [{ kind: "missing", part: whenPart, sentence: copy.timeMissing } as const] : []),
      ...(!draft.note.trim() ? [{ kind: "missing", part: notePart, sentence: copy.noteMissing } as const] : []),
    ];

    // What a confirmed change did, from the send's own record, never from text typed since.
    const confirmed = (send: ChangeSend, remains: string[]): Result => {
      setSavedNotes((current) => ({ ...current, [slot]: send.body.note }));
      return { state: "confirmed", cap: copy.done[kind](name), at: new Date().toISOString(), effects: send.effects, remains };
    };
    const finished = (send: ChangeSend) => {
      kept.setPending(null);
      // The draft ends only if it is still what was sent; text added since stays, as a draft on the newer session.
      if (JSON.stringify(kept.value) === JSON.stringify(send.sent)) kept.end();
    };
    const unknown = (send: ChangeSend): Result => ({
      state: "unknown",
      cap: copy.unknownCap,
      requestId: send.key,
      sentence: copy.unknown(name),
      check: {
        verb: copy.check,
        note: copy.checkNote,
        run: async () => {
          let checked: Result;
          try {
            const receipt = await checkCommit(send.key);
            if (receipt.state === "confirmed") {
              finished(send);
              const withheld = withheldNotice(receipt.receipt.result.withheld);
              checked = confirmed(send, withheld ? [withheld] : []);
            } else if (receipt.state === "not_committed") {
              // A definite no: said, with the draft kept for another send.
              kept.setPending(null);
              checked = { state: "refused", cap: NOT_DONE[kind], sentence: copy.notCommitted, draft: "kept" };
            } else checked = unknown(send);
          } catch {
            checked = unknown(send);
          }
          if (checked.state === "confirmed") await onChanged();
          return settle(checked);
        },
      },
    });

    return {
      parts,
      commit: {
        verb: copy.verb[kind],
        cap: copy.cap[kind],
        effects,
        blockers,
        ...(kind === "move" ? { revises: { part: whenPart, unchanged: () => unchanged } } : {}),
        run: async (requestId) => {
          // Sent from the version the draft began from: a newer session refuses, and is compared, never adopted (R4).
          const send: ChangeSend = {
            key: requestId,
            sent: draft,
            body: {
              note: draft.note.trim(),
              tell_students: draft.email && !unpublished,
              expected_updated_at: kept.began ?? session.updated_at,
              ...(kind === "move" ? { session_date: draft.date, start_time: draft.time } : {}),
            },
            // What happened to email is the server's receipt line, never a promise repeated here.
            effects: effects().filter((effect) => !effect.optional && effect.id !== "email").map((effect) => effect.sentence),
          };
          kept.setPending(send);
          let next: Result;
          try {
            const receipt = await sessionPortalApi.change(courseId, session.id, kind, send.body, requestId);
            finished(send);
            await onChanged();
            next = confirmed(send, [receipt.delivery.message]);
          } catch (error) {
            if (!(error instanceof ApiError) || error.status >= 500) next = unknown(send);
            else {
              kept.setPending(null);
              // A newer session: read it, so the draft shows the comparison and waits for the person's choice (R4).
              if (error.status === 409) void onChanged();
              next = { state: "refused", cap: NOT_DONE[kind], sentence: error.message, draft: "kept" };
            }
          }
          return settle(next);
        },
      },
      // After a reload, a send still unanswered comes back as Not confirmed with Check on its own key (W1).
      result: results[slot] ?? (kept.pending ? unknown(kept.pending) : { state: "none" }),
    };
  }, [courseId, timezone, unpublished, onChanged, ofDraft, results, savedNotes, id]);
}
